import React, { useCallback, useEffect, useMemo } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'

import FinalizeTaskDialog from '../FinalizeTaskController/FinalizeTaskDialog'

import useSubmitTask from 'hooks/useSubmitTask'

import TaskHeaderController from 'controllers/TaskManager/TaskSubControllers/TaskHeader/TaskHeader'
import AlertBox from 'components/AlertBox/AlertBox'

import styles from '../Dialog.module.sass'
import TaskMenuController from '../TaskMenu/TaskMenuController'
import InfoList from 'components/InfoList/InfoList'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'
import HFTextInput from 'components/HFInputs/HFTextInput'

import { useReload } from 'store'
import { ticketsSchema } from './TicketsSchema'
import Ticket from 'models/Ticket'
import { useFetch } from 'store'
import { prefixPlusSignIfLongerThan9Digits } from 'utils/computePhoneNumber'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import Icon from '@jimmyboys/liita-ui/dist/components/Icon'

const TicketsTaskController = (props) => {
	const { data, dismiss, id, resolve } = props
	const processId = data?.processId
	const { i18n, showDialog } = useGlobal()
	const reload = useReload()

	const methods = useForm({
		mode: 'onSubmit',
		resolver: zodResolver(ticketsSchema),
		shouldUnregister: true,
	})
	const { handleSubmit, formState, reset, getValues } = methods

	const ticketUri = `/metadata/tickets/${data?.ticketMetadata?.ticketId}`

	const { contents } = useFetch(
		data?.ticketMetadata?.ticketId && ticketUri,
		data?.ticketMetadata?.ticketId && Ticket.fromJSON
	)

	const { author = '', description = '' } = contents

	const { error, submitting, approveTask } = useSubmitTask(
		data?.id,
		formState?.errors
	)

	const onSubmit = (data) => {
		approveTask(data, 'submit')
			.then(() => {
				resolve()
				reload([
					`/clinical_processes/${processId}/tasks`,
					`/clinical_processes/${processId}/tickets`,
				])
			})
			.catch(() => {})
	}

	const customError = error?.map((err) => {
		return {
			title: i18n`Error`,
			text: err,
		}
	})

	useEffect(() => {
		if (
			data.formState &&
			!formState.isDirty &&
			formState.submitCount === 0
		) {
			reset && data.formState && reset(data.formState)
		}
	}, [reset, data.formState, formState.isDirty, formState.submitCount])

	const showCallButton = data?.subType === 'ContactClaimant'

	const handleCallButton = (event) => {
		const mobileNumber = document.getElementById('contact-mobile-number')
			?.dataset?.value
		const telephoneNumber = document.getElementById(
			'contact-telephone-number'
		)?.dataset?.value

		const contact = mobileNumber || telephoneNumber
		if (contact) {
			window.location = prefixPlusSignIfLongerThan9Digits(contact)
			event.preventDefault()
		}
	}

	const addAssociatedTicket = useCallback(() => {
		return showDialog(
			'TicketRequestDialog',
			i18n`Ticket Request Dialog ${contents.id}`,
			{
				processId: contents.processId,
				ticketId: contents.id,
			},
			false,
			undefined,
			'center',
			'request-ticket-dialog'
		)
			.then(() => {
				reload([
					`/clinical_processes/${contents.processId}/tickets`,
					`/clinical_processes/${contents.processId}/tasks`,
					`/metadata/tickets/${contents.id}`,
				])
			})
			.catch(() => null)
	}, [contents, i18n, reload, showDialog])

	const hideSubmit = useMemo(() => {
		if (contents?.childs?.length) {
			const unfinishedChild = contents.childs.some(
				(child) => ![5, 6].includes(child.ticketStatus.id)
			)

			return unfinishedChild
		}

		return false
	}, [contents?.childs])

	return (
		<>
			{submitting && (
				<FinalizeTaskDialog
					dismiss={dismiss}
					submitting={submitting}
					task={data}
				/>
			)}
			<FormProvider {...methods}>
				<div className={styles['form-container']}>
					<TaskHeaderController task={data} />
					<form
						id={`form_${id}`}
						className={styles['form']}
						onSubmit={handleSubmit(onSubmit)}
					>
						{customError && customError.length > 0 && (
							<AlertBox
								className={styles['alert-box']}
								type='error'
								items={customError}
							/>
						)}
						{hideSubmit && (
							<AlertBox
								className={styles['alert-box']}
								type='warning'
								items={[
									{
										title: i18n`Associated tickets`,
										text: i18n`This task can only be submitted when the associated tickets are finished.`,
									},
								]}
							/>
						)}
						{contents?.parentTicket && (
							<AlertBox
								className={styles['alert-box']}
								type='info'
								items={[
									{
										title: i18n`Secondary ticket`,
										text: i18n`This ticket is child from #${contents?.parentTicket.id} created by ${contents?.parentTicket?.createdBy}.`,
									},
								]}
							/>
						)}
						<InfoList
							items={[
								{
									title: i18n`Openned by`,
									text: author,
									icon: 'profile',
								},
								{
									title: i18n`Description`,
									text: description,
									icon: 'info',
								},
							]}
						/>
						<fieldset>
							{showCallButton && (
								<button
									className={`${styles['call-button']} ${styles['--bigger']}`}
									onClick={handleCallButton}
								>
									<HStack>
										<Icon
											name='phone'
											width='20px'
											height='20px'
										/>
										<h5>{i18n`Call injured`}</h5>
									</HStack>
								</button>
							)}
							<VStack className={styles['observation-container']}>
								<h5>{i18n`Answer`}</h5>
								<HFTextInput
									name='answer'
									placeholder={i18n`Write your answer`}
									multiline={true}
									rows={5}
									noResize
								/>
							</VStack>
						</fieldset>
					</form>
				</div>
			</FormProvider>
			<TaskMenuController
				data={{
					formId: id,
					options: ['postpone', 'forward'],
					submitting,
					submissionType: 'submit',
					getValues,
					addAssociatedTicket: contents?.parentTicket
						? null
						: addAssociatedTicket,
					hideSubmit,
				}}
			/>
		</>
	)
}

export default TicketsTaskController
